// @flow
import React from 'react';
import { Trans } from 'react-i18next';

import { ToastTypes, ToastStates } from 'common/components/core/toast/Toast';
import FontIcon from 'react-toolbox/lib/font_icon/FontIcon';
import DataUtils from 'common/utils/DataUtils';
import TimeUtils, { DateTimeFormat } from 'common/utils/TimeUtils';
import { ApiError } from 'common/modules/api';
import I18nHelper from 'common/helpers/I18nHelper';

import { ExportStatus } from 'common/enums/ExportEnums';

import theme from './AsyncExportHandler.scss';

const translationsNamespace = ['entityManagement'];
const nsTranslate = I18nHelper.getTranslate(translationsNamespace);

export default class AsyncExportHandler {
    constructor(exportApi, exportStatusApi) {
        this.toast = null;
        this.exportId = null;
        this.exportApi = exportApi;
        this.exportStatusApi = exportStatusApi;
        this.email = null;
        this.queued = false;
        this.options = {};
    }

    setUpPollingApi = () => {
        const {
            onError,
            onExportTimeout,
            errorOnTimeoutDuration
        } = this.options;
        if (this.timer) {
            clearTimeout(this.timer);
        }

        if (this.exportId) {
            this.timer = setInterval(() => {
                this.getEntityExportStatus()
                    .then(response => {
                        // invoke toast rendering cb
                        const {
                            status: { status },
                            statusUpdates
                        } = response;
                        if (
                            errorOnTimeoutDuration &&
                            status === ExportStatus.QUEUED &&
                            TimeUtils.diff({
                                startDate: statusUpdates[0].updatedOn,
                                endDate: TimeUtils.now(
                                    true,
                                    DateTimeFormat.UTC_FORMAT
                                ),
                                unit: 'm'
                            }) > errorOnTimeoutDuration
                        ) {
                            clearInterval(this.timer);
                            this.hideToastAction({ id: this.toast });
                            this.queued = false;
                            onExportTimeout?.();
                        } else {
                            this.handleToastMessage(response);
                        }
                    })
                    .catch(error => {
                        onError && onError({ ...error });
                        throw new Error(error);
                    });
            }, 1500);
        }
    };

    getEntityExportStatus = () => {
        const params = { clientId: this.clientId, exportId: this.exportId };
        return this.exportStatusApi.fetch(params);
    };

    buildQueuedToast = () => {
        const exportTranslatePrefix = 'entityExport.exportToast';
        const { queuedMessage } = this.options;
        const message = (
            <div>
                <div className={theme.queuedMessage}>
                    {queuedMessage
                        ? queuedMessage
                        : this.translate(
                              `${exportTranslatePrefix}.message.queued`,
                              {
                                  exportId: this.exportId,
                                  master: this.master
                              }
                          )}
                </div>
                {this.email ? (
                    <div>
                        {this.translate(
                            `${exportTranslatePrefix}.subText.queued`,
                            {
                                email: this.email
                            }
                        )}
                    </div>
                ) : null}
            </div>
        );

        const type = ToastTypes.Info;

        return {
            inverted: true,
            customContent: message,
            timeout: null,
            type
        };
    };

    buildSuccessToast = reportUrl => {
        const exportTranslatePrefix = 'entityExport.exportToast';
        const {
            exportId,
            options: { showExportId = true },
            master
        } = this;
        const customContent = (
            <div className={theme.message}>
                <Trans
                    i18nKey={`entityManagement:${exportTranslatePrefix}.message.success`}
                    className={theme.inline}
                >
                    Your export {showExportId ? <b>{{ exportId }}</b> : ''} for
                    {{ master }} is ready.
                </Trans>
                &nbsp;
                <Trans
                    i18nKey={`entityManagement:${exportTranslatePrefix}.subText.success`}
                    className={theme.inline}
                >
                    To download click <a href={reportUrl}>here</a>
                </Trans>
            </div>
        );

        const type = ToastTypes.Success;
        return {
            inverted: true,
            customContent,
            timeout: null,
            type
        };
    };

    onToastStateChange = ({ id, status }) => {
        if (
            status === ToastStates.Deleted &&
            !this.options.retainPollingOnClose
        ) {
            clearTimeout(this.timer);
            return;
        }
        this.toast = id;
    };

    handleToastMessage = exportStatus => {
        const {
            status: { status },
            reportURL
        } = exportStatus;
        const { onSuccess } = this.options;
        const customAction = ({ hideToast }) => (
            <FontIcon
                value="close"
                onClick={hideToast}
                className={theme.exportHandlerCloseIcon}
            />
        );
        let toastObject = {};
        /* Once user closes the queued toast, we need not render it again
        on subsequent polls when the request is still queued */
        let shouldShowToast = !this.queued;
        if (status === ExportStatus.QUEUED) {
            toastObject = this.buildQueuedToast();
            this.queued = true;
        } else if (status === ExportStatus.DONE) {
            clearTimeout(this.timer);
            onSuccess && onSuccess({ ...exportStatus });
            this.queued = false;
            shouldShowToast = true;
            toastObject = this.buildSuccessToast(reportURL);
        } else {
            this.hideToastAction({ id: this.toast });
            this.queued = false;
            return;
        }
        toastObject.customAction = customAction;
        toastObject.onToastStateChange = this.onToastStateChange;

        if (shouldShowToast) {
            if (!DataUtils.isEmpty(this.toast)) {
                this.updateToastAction(toastObject, { id: this.toast });
            } else {
                this.showToastAction(toastObject);
            }
        }
    };

    processResponse = response => {
        if (!(response instanceof ApiError)) {
            this.exportId = response.exportId;
            // invoke polling export status
            this.setUpPollingApi();
            return;
        }
    };

    makeExportRequest(requestParams) {
        const params = { clientId: this.clientId };
        const { onSuccess, onError, onStart } = this.options;
        onStart && onStart();
        return this.exportApi
            .post(params, requestParams)
            .then(response => {
                onSuccess && onSuccess();
                this.processResponse(response);
                this.handleToastMessage(response);
            })
            .catch(err => {
                onError && onError({ ...err });
                throw err;
            });
    }

    initialiseAndMakeExportRequest(
        requestParams,
        clientId,
        showToastAction,
        updateToastAction,
        hideToastAction,
        options = {}
    ) {
        this.showToastAction = showToastAction;
        this.updateToastAction = updateToastAction;
        this.hideToastAction = hideToastAction;
        this.translate = nsTranslate;
        this.clientId = clientId;
        this.master = options?.entityMasterName;
        this.options = options;

        const { emailAddresses } = requestParams;
        this.email = emailAddresses?.[0].emailAddress;

        return this.makeExportRequest(requestParams);
    }
}
